import { SUBPLOT_COUNT } from '../../../rules/subplotGrid'
import { PEEK_RING_COLORS, letterColorIndex } from './geometry'

// ─── Hex board — intel peek ───────────────────────────────────────────────────
// While the header eye is held (oppPeek), every drone and corridor pill lifts
// off the board and the Hive's intel lights up — run once per Hive: yours on
// the enemy's, and the enemy's on yours.
//
// Located structures — a lettered structure the attacker has seen at least
// one cell of (cratered or drone-scanned). Its stamps draw nothing and are
// hidden (hiddenStamps) while the eye is held: it needs no search.
//
// Labelled structures (located): their Hive shows only what you've seen of
// it — a scanned, standing hex filled in the letter's hue, a cratered one
// left to its X. Your own Hive ({ own: true }) knows your structures: every
// one the enemy has a lettered stamp on, or has found, has all its standing
// hexes filled. Either way one hex carries the letter (a filled one, with no
// stamp showing, where there is one), and a dead structure draws nothing —
// its craters say it. Never pass own for their Hive: it would draw their
// hidden structures.
//
// Every other live bearing is listed for the board to draw (PeekSonar: one
// hexagon through its ring, a sonar sweep, dots where hexagons cross), in
// its structure's colour (same letter, same hue everywhere); the stamp takes
// the hue too. Dead stamps (their structure destroyed) draw nothing.
//
// Returns null when nothing is lit, else { centerColor: Map s→colour,
// bearings: [{ s, d, letter, color }], located: Map s→{ letter, color, fill,
// label } (fill = paint the hex; label = it carries the letter),
// hiddenStamps: Set s, cratered: [s] (where the board quiets the hexagon
// lines) }.
export function buildPeekIntel(hf, { own = false } = {}) {
    if (!hf?.subplots) return null
    const centerColor = new Map()
    const bearings = []
    const located = new Map()
    const hiddenStamps = new Set()
    const seen = (cell) => !!(cell?.destroyed || cell?.scanned || cell?.revealed)
    const letters = hf.intelLetters || {}
    const hue = (idx) => PEEK_RING_COLORS[idx % PEEK_RING_COLORS.length]

    // Structures the attacker has eyes on, by letter.
    const seenOf = {}
    for (const [id, letter] of Object.entries(letters)) {
        const cells = (hf.structures?.[id]?.footprint ?? []).filter(s => seen(hf.subplots[s]))
        if (cells.length) seenOf[letter] = { id, cells }
    }
    const locatedLetters = new Set(Object.keys(seenOf))
    // A stamp still showing (one naming a located structure hides) — the
    // letter goes on a hex without one, so it never covers a bearing.
    const stampShows = (s) => hf.subplots[s]?.distance != null && !locatedLetters.has(hf.subplots[s].distanceLetter)
    const isDead = (st) => st.footprint.every(s => hf.subplots[s]?.destroyed || hf.subplots[s]?.hp <= 0)
    const mark = (letter, cells) => {
        if (!cells.length) return
        const color = hue(letterColorIndex(letter))
        // The letter prefers a filled hex, then any hex, without a stamp showing.
        const labelS = cells.find(c => c.fill && !stampShows(c.s)) ?? cells.find(c => !stampShows(c.s)) ?? cells[0]
        cells.forEach(c => located.set(c.s, { letter, color, fill: c.fill, label: c === labelS }))
    }
    if (!own) {
        // Their Hive: only what you've seen. A cratered hex keeps its X (no
        // fill); a scanned, standing one is filled.
        for (const [letter, { id, cells }] of Object.entries(seenOf)) {
            if (isDead(hf.structures[id])) continue
            mark(letter, cells.map(s => ({ s, fill: !hf.subplots[s].destroyed })))
        }
    }

    // Legacy letterless stamps colour per-stamp instead, from indices past
    // the letter ledger so they don't shadow a structure's hue.
    let legacyIdx = Object.keys(letters).length
    for (let s = 0; s < SUBPLOT_COUNT; s++) {
        const cell = hf.subplots[s]
        const d = cell?.distance
        if (d == null) continue
        const letter = cell.distanceLetter
        if (letter != null && locatedLetters.has(letter)) {
            hiddenStamps.add(s)
            continue
        }
        const color = hue(letter != null ? letterColorIndex(letter) : legacyIdx++)
        if (cell.distanceDead) continue
        centerColor.set(s, color)
        bearings.push({ s, d, letter: letter ?? null, color })
    }
    if (own) {
        // Your Hive: you know your structures. Every one the enemy has a
        // lettered stamp on (or has found) has its standing hexes filled;
        // its destroyed hexes keep their X.
        const idOf = Object.fromEntries(Object.entries(letters).map(([id, l]) => [l, id]))
        const named = new Set([...locatedLetters, ...bearings.map(b => b.letter).filter(l => l != null)])
        for (const letter of named) {
            const st = hf.structures?.[idOf[letter]]
            if (!st || isDead(st)) continue
            mark(letter, st.footprint.filter(s => !hf.subplots[s]?.destroyed).map(s => ({ s, fill: true })))
        }
    }
    const cratered = []
    hf.subplots.forEach((cell, s) => { if (cell?.destroyed) cratered.push(s) })
    return centerColor.size || located.size ? { centerColor, bearings, located, hiddenStamps, cratered } : null
}
